import React from 'react';
import { Link } from 'react-router-dom';
import { HelpCircle, ArrowUpRight } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import { mockFAQs } from '../mockData';

// User-supplied OPEN sign image — neutral merchant retail/hospitality signal, warm cream tones.
const FAQ_IMAGE = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/5kntx5dt_OPEN%20SIGN%20IMAGE.jpg';

const FAQ = () => {
  return (
    <section
      id="faq"
      className="relative py-20 sm:py-24 px-4 sm:px-6 lg:px-8 bg-[#faf6f0] overflow-hidden"
      data-testid="home-faq-section"
    >
      {/* soft decorative warmth */}
      <div className="pointer-events-none absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#ef5b25]/8 blur-3xl" aria-hidden="true" />
      <div className="pointer-events-none absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#1F4E79]/8 blur-3xl" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        {/* ── LEFT: compact accordion ── */}
        <div className="lg:col-span-7">
          <p className="text-[11px] font-bold text-[#ef5b25] uppercase tracking-[0.22em] mb-3">
            Frequently Asked Questions
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold text-[#0a1929] leading-tight tracking-tight mb-3">
            Answers to the<br className="hidden sm:block" /> questions we hear most.
          </h2>
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-xl mb-8">
            Everything you need to know about getting started, payments and ongoing support — in plain English.
          </p>

          <Accordion
            type="single"
            collapsible
            className="divide-y divide-[#0a1929]/10 border-y border-[#0a1929]/10"
            data-testid="home-faq-accordion"
          >
            {mockFAQs.map((faq) => (
              <AccordionItem
                key={faq.id}
                value={`item-${faq.id}`}
                className="border-0"
                data-testid={`home-faq-item-${faq.id}`}
              >
                <AccordionTrigger className="py-4 text-left text-[15px] sm:text-base font-semibold text-[#0a1929] hover:no-underline hover:text-[#1F4E79] transition-colors duration-200 [&[data-state=open]]:text-[#1F4E79]">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="pb-4 pr-6 text-[14px] sm:text-[15px] text-gray-600 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* ── RIGHT: editorial image + floating glass-morph "Need help?" card ── */}
        <div className="lg:col-span-5">
          <div className="relative max-w-md mx-auto lg:mx-0 lg:sticky lg:top-28">
            {/* soft glow behind card */}
            <div className="absolute -inset-4 bg-gradient-to-br from-[#ef5b25]/15 to-[#1F4E79]/15 rounded-3xl blur-2xl pointer-events-none" aria-hidden="true" />

            {/* image card — image stays clipped to rounded corners, glass card can hang below */}
            <div className="relative aspect-square">
              <div className="absolute inset-0 rounded-2xl overflow-hidden border border-white/60 shadow-2xl">
                <img
                  src={FAQ_IMAGE}
                  alt="Open for business — independent merchant welcoming customers"
                  className="w-full h-full object-cover"
                  data-testid="home-faq-image"
                />
                {/* subtle gradient for legibility under the glass card */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a1929]/55 via-[#0a1929]/10 to-transparent pointer-events-none" aria-hidden="true" />
              </div>

              {/* glass-morph "Accepting Payments FAQs" card — sits half-on the image, half-below.
                  Uses dark glass-morph (same family as the Cookie banner) so it reads cleanly on
                  both the image (upper half) and the cream section bg (lower half). */}
              <div
                className="absolute -bottom-14 left-5 right-5 sm:right-auto sm:max-w-[290px] rounded-xl bg-[#0a1929]/85 backdrop-blur-xl border border-white/15 shadow-2xl p-4"
                data-testid="home-faq-help-card"
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-white/25 backdrop-blur-md border border-white/40 flex items-center justify-center">
                    <HelpCircle size={14} className="text-white" strokeWidth={2.25} />
                  </span>
                  <p className="text-[10px] font-bold text-white uppercase tracking-[0.2em] leading-tight">Have questions on<br />accepting payments?</p>
                </div>
                <p className="text-white/95 text-[13.5px] leading-snug mb-2.5">
                  Visit our Accepting Payments FAQs for guidance on taking card payments.
                </p>
                <Link
                  to="/accepting-payments-faqs"
                  className="inline-flex items-center gap-1.5 text-white font-bold text-[13px] hover:gap-2.5 transition-all duration-200"
                  data-testid="home-faq-contact-link"
                >
                  See Accepting Payments FAQs
                  <ArrowUpRight size={14} strokeWidth={2.5} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
